import React, { useState, useEffect, useCallback } from 'react';
import API from '../../services/api';
import BikeLifeCycleView from './BikeLifeCycleView';

const InventoryListSection = () => {
    // Local date properly generate karne ke liye helper function ('YYYY-MM-DD')
    const getLocalDateStr = () => {
        const d = new Date();
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const todayStr = getLocalDateStr();

    const [stock, setStock] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalRecords, setTotalRecords] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [search, setSearch] = useState('');
    
    // Default by-default aaj ki date set hai
    const [startDate, setStartDate] = useState(todayStr);
    const [endDate, setEndDate] = useState(todayStr);

    const [viewingBikeId, setViewingBikeId] = useState(null);
    const limit = 10;

    // 100% Exact Date Format (Timezone shift / 1 din peeche hone se bachne ke liye)
    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        return String(dateStr).split('T')[0];
    };

    const fetchStock = useCallback(async () => {
        setLoading(true);
        try {
            const res = await API.get(`/v1/inventory/live-stock`, {
                params: { 
                    page: currentPage, 
                    limit: limit, 
                    search: search.trim(),
                    start_date: startDate,
                    end_date: endDate
                }
            });
            setStock(res.data.data || []);
            setTotalRecords(res.data.total || 0);
        } catch (err) { 
            console.error("Inventory error:", err); 
        } finally { 
            setLoading(false); 
        }
    }, [currentPage, search, startDate, endDate]);

    useEffect(() => {
        const delayDebounce = setTimeout(() => fetchStock(), 300);
        return () => clearTimeout(delayDebounce);
    }, [fetchStock]);

    const handleResetToToday = () => {
        setStartDate(todayStr);
        setEndDate(todayStr);
        setSearch('');
        setCurrentPage(1);
    };

    const totalPages = Math.ceil(totalRecords / limit) || 1;

    return (
        <div>
            {/* Filter Card */}
            <div style={styles.filterCard}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: '15px', marginBottom: '15px' }}>
                    <div>
                        <label style={styles.label}>Start Date</label>
                        <input 
                            type="date" 
                            style={styles.input} 
                            value={startDate} 
                            onChange={(e) => { setStartDate(e.target.value); setCurrentPage(1); }} 
                        />
                    </div>
                    <div>
                        <label style={styles.label}>End Date</label>
                        <input 
                            type="date" 
                            style={styles.input} 
                            value={endDate} 
                            onChange={(e) => { setEndDate(e.target.value); setCurrentPage(1); }} 
                        />
                    </div>
                    <div>
                        <label style={styles.label}>Quick Search (Engine, Chassis, Model)</label>
                        <input 
                            placeholder="Search Engine #, Chassis # or Model..."
                            style={styles.input}
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                        />
                    </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #e2e8f0', paddingTop: '10px' }}>
                    <span style={{ fontSize: '13px', color: '#64748b', fontWeight: 'bold' }}>
                        Total Units Found: <span style={{ color: '#0f172a' }}>{totalRecords}</span>
                    </span>
                    <button 
                        onClick={handleResetToToday}
                        style={styles.resetBtn}
                    >
                        Reset to Today
                    </button>
                </div>
            </div>

            {/* Table Card */}
            <div style={styles.tableCard}>
                <table style={styles.table}>
                    <thead style={styles.thead}>
                        <tr>
                            <th style={styles.th}>Purchase Date</th>
                            <th style={styles.th}>Model & Color</th>
                            <th style={styles.th}>Chassis / Engine</th>
                            <th style={styles.th}>Cost</th>
                            <th style={styles.th}>Status</th>
                            <th style={styles.th}>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="6" style={styles.loader}>Loading Inventory Data...</td></tr>
                        ) : stock.length > 0 ? (
                            stock.map((item) => (
                                <tr key={item.id} style={styles.tr}>
                                    <td style={styles.td}>
                                        <div style={{ fontSize: '12px', fontWeight: '600', color: '#334155' }}>
                                            {formatDate(item.purchase_date)}
                                        </div>
                                    </td>
                                    <td style={styles.td}>
                                        <div style={{ fontWeight: 'bold', color: '#1e293b' }}>{item.model_name}</div>
                                        <div style={{ fontSize: '11px', color: '#64748b' }}>{item.color}</div>
                                    </td>
                                    <td style={styles.td}>
                                        <div style={{ fontSize: '12px', color: '#475569' }}>C: {item.chassis_number}</div>
                                        <div style={{ fontSize: '12px', color: '#475569' }}>E: {item.engine_number}</div>
                                    </td>
                                    <td style={{ ...styles.td, fontWeight: 'bold', color: '#059669' }}>
                                        Rs. {Number(item.purchase_price || 0).toLocaleString()}
                                    </td>
                                    <td style={styles.td}>
                                        <span style={styles.statusBadge}>{item.stock_status}</span>
                                    </td>
                                    <td style={styles.td}>
                                        <button 
                                            onClick={() => {
                                                const rawId = item.bike_master_id || item.id;
                                                const cleanId = String(rawId).split(':')[0]; 
                                                setViewingBikeId(cleanId);
                                            }} 
                                            style={styles.viewBtn}
                                        >
                                            View Profile
                                        </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="6" style={{ padding: '30px', textAlign: 'center', color: '#64748b', fontWeight: 'bold' }}>
                                    No records found for selected date/range.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
                <div style={styles.paginationRow}>
                    <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)} style={styles.pageBtn}>Prev</button>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>Page {currentPage} of {totalPages}</span>
                    <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)} style={styles.pageBtn}>Next</button>
                </div>
            </div>

            {/* Bike Life Cycle Dialog */}
            {viewingBikeId && (
                <BikeLifeCycleView 
                    bikeId={viewingBikeId} 
                    onClose={() => setViewingBikeId(null)} 
                />
            )}
        </div>
    );
};

const styles = {
    filterCard: { background: '#fff', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '20px' },
    label: { display: 'block', fontSize: '11px', fontWeight: 'bold', color: '#475569', textTransform: 'uppercase', marginBottom: '5px' },
    input: { width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '13px', boxSizing: 'border-box', background: '#fff' },
    resetBtn: { padding: '6px 12px', background: '#f1f5f9', color: '#0f172a', border: '1px solid #cbd5e1', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' },
    tableCard: { background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' },
    table: { width: '100%', borderCollapse: 'collapse' },
    thead: { background: '#f8fafc', borderBottom: '1px solid #e2e8f0' },
    th: { padding: '15px', textAlign: 'left', fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: 'bold' },
    tr: { borderBottom: '1px solid #f1f5f9', transition: '0.2s' },
    td: { padding: '15px', fontSize: '13px' },
    loader: { padding: '40px', textAlign: 'center', color: '#64748b', fontWeight: 'bold' },
    statusBadge: { background: '#dcfce7', color: '#166534', padding: '4px 10px', borderRadius: '6px', fontSize: '10px', fontWeight: 'bold' },
    viewBtn: { padding: '8px 15px', background: '#1e293b', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px' },
    paginationRow: { padding: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fff', borderTop: '1px solid #e2e8f0' },
    pageBtn: { padding: '6px 14px', borderRadius: '6px', border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }
};

export default InventoryListSection;